import "./chat.css";
import { useEffect, useState, useRef, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  collection,
  query,
  where,
  onSnapshot,
  orderBy,
  addDoc,
  getDoc,
  doc,
  limit,
  getDocs,
  writeBatch,
  setDoc,
  serverTimestamp,
  startAfter,
  getCountFromServer,
} from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { db, storage, auth } from "../../services/firebase";
import { sendBotResponse, ensureBotUser, shouldBotRespond } from "../../services/botService";
import {
  IoSend,
  IoDocument,
  IoClose,
  IoAttach,
  IoArrowBack,
  IoSearchOutline,
  IoChatbubbleEllipses,
  IoCheckmarkDone,
  IoCheckmark,
  IoChevronUp,
} from "react-icons/io5";
import { Spinner } from "react-bootstrap";
import { format } from "date-fns";

const Chat = () => {
  const navigate = useNavigate();
  const { userId } = useParams();
  const [users, setUsers] = useState(null);
  const [messages, setMessages] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [newMessage, setNewMessage] = useState("");
  const [attachment, setAttachment] = useState(null);
  const [isAdmin, setIsAdmin] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [attachmentPreview, setAttachmentPreview] = useState(null);
  const [isLoadingUsers, setIsLoadingUsers] = useState(true);
  const [isLoadingMessages, setIsLoadingMessages] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [error, setError] = useState("");
  const [unreadCounts, setUnreadCounts] = useState({});
  const [previewModal, setPreviewModal] = useState({
    show: false,
    src: "",
    type: "",
  });
  const [lastMessageDoc, setLastMessageDoc] = useState(null);
  const [hasMoreMessages, setHasMoreMessages] = useState(true);
  const [isLoadingOlderMessages, setIsLoadingOlderMessages] = useState(false);
  const [lastMessages, setLastMessages] = useState({});
  const [totalMessageCounts, setTotalMessageCounts] = useState({});

  const currentUser = auth.currentUser;
  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const [isMobileView, setIsMobileView] = useState(window.innerWidth <= 768);

  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      setIsMobileView(window.innerWidth <= 768);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Initialize user admin status
  useEffect(() => {
    if (!currentUser) return;

    const userDocRef = doc(db, "users", currentUser.uid);
    getDoc(userDocRef)
      .then((docSnap) => {
        if (docSnap.exists()) {
          setIsAdmin(docSnap.data().isAdmin || false);
        }
      })
      .catch((err) => {
        console.error("Error fetching user data:", err);
        setError("Failed to load user information");
      });
  }, [currentUser]);

  // Enhanced search with debouncing
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState("");

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Filter users with enhanced search
  const filteredUsers = users
    ? users.filter(
        (user) =>
          user.name
            ?.toLowerCase()
            .includes(debouncedSearchQuery.toLowerCase()) ||
          user.email
            ?.toLowerCase()
            .includes(debouncedSearchQuery.toLowerCase()) ||
          user.displayName
            ?.toLowerCase()
            .includes(debouncedSearchQuery.toLowerCase())
      )
    : [];

  // Mark messages as read
  const markMessagesAsRead = useCallback(async () => {
    if (!selectedUser) return;

    try {
      const messagesQuery = query(
        collection(db, "messages"),
        where("receiver", "==", currentUser.uid),
        where("sender", "==", selectedUser.uid),
        where("read", "==", false)
      );

      const unreadMessages = await getDocs(messagesQuery);
      const batch = writeBatch(db);

      unreadMessages.docs.forEach((doc) => {
        batch.update(doc.ref, { read: true });
      });

      if (unreadMessages.docs.length > 0) {
        await batch.commit();
        // Update local unread counts immediately
        setUnreadCounts((prev) => {
          const newCounts = { ...prev };
          newCounts[selectedUser.uid] = 0;
          return newCounts;
        });
      }
    } catch (err) {
      console.error("Error marking messages as read:", err);
    }
  }, [selectedUser, currentUser]);

  // Load unread counts for all users with optimized Firebase queries
  const loadUnreadCounts = useCallback(async () => {
    if (!currentUser || !users) return;

    try {
      const counts = {};

      // Get unread counts for each user efficiently
      for (const user of users) {
        const unreadQuery = query(
          collection(db, "messages"),
          where("receiver", "==", currentUser.uid),
          where("sender", "==", user.uid),
          where("read", "==", false)
        );

        const countSnapshot = await getCountFromServer(unreadQuery);
        const count = countSnapshot.data().count;
        if (count > 0) {
          counts[user.uid] = count;
        }
      }

      setUnreadCounts(counts);
    } catch (err) {
      console.error("Error loading unread counts:", err);
    }
  }, [currentUser, users]);

  useEffect(() => {
    if (users && users.length > 0) {
      loadUnreadCounts();
    }
  }, [users, loadUnreadCounts]);

  // Real-time listener for new messages to update unread counts
  useEffect(() => {
    if (!currentUser || isAdmin === null) return;

    const unreadQuery = query(
      collection(db, "messages"),
      where("receiver", "==", currentUser.uid),
      where("read", "==", false)
    );

    const unsubscribe = onSnapshot(
      unreadQuery,
      (snapshot) => {
        try {
          const counts = {};
          snapshot.docs.forEach((doc) => {
            const message = doc.data();
            const senderId = message.sender;
            counts[senderId] = (counts[senderId] || 0) + 1;
          });
          setUnreadCounts(counts);
        } catch (err) {
          console.error("Error loading unread counts:", err);
        }
      },
      (err) => {
        console.error("Error loading unread counts:", err);
      }
    );

    return () => unsubscribe();
  }, [currentUser, isAdmin]);

  // Load last messages for all users
  const loadLastMessages = useCallback(
    async (usersList) => {
      if (!currentUser || !usersList.length) return {};

      try {
        const lastMessagesData = {};

        for (const user of usersList) {
          const messagesQuery = query(
            collection(db, "messages"),
            where("sender", "in", [currentUser.uid, user.uid]),
            where("receiver", "in", [currentUser.uid, user.uid]),
            orderBy("timestamp", "desc"),
            limit(1)
          );

          const snapshot = await getDocs(messagesQuery);
          if (!snapshot.empty) {
            const lastMessage = snapshot.docs[0].data();
            lastMessagesData[user.uid] = {
              text: lastMessage.text || "Attachment",
              timestamp: lastMessage.timestamp,
              sender: lastMessage.sender,
            };
          }
        }

        setLastMessages(lastMessagesData);
        return lastMessagesData;
      } catch (err) {
        console.error("Error loading last messages:", err);
        return {};
      }
    },
    [currentUser]
  );

  // Load users with enhanced sorting and last messages
  useEffect(() => {
    if (isAdmin === null) return;

    const usersQuery = query(
      collection(db, "users"),
      where("isAdmin", "==", !isAdmin)
    );

    const unsubscribe = onSnapshot(
      usersQuery,
      async (snapshot) => {
        try {
          const usersList = snapshot.docs.map((doc) => ({
            uid: doc.id,
            ...doc.data(),
          }));

          // Load last messages for sorting
          const lastMessagesData = await loadLastMessages(usersList);

          // Sort users by last message timestamp
          const sortedUsers = usersList.sort((a, b) => {
            const aLastMessage = lastMessagesData[a.uid];
            const bLastMessage = lastMessagesData[b.uid];

            if (!aLastMessage && !bLastMessage) return 0;
            if (!aLastMessage) return 1;
            if (!bLastMessage) return -1;

            return (
              bLastMessage.timestamp.toMillis() -
              aLastMessage.timestamp.toMillis()
            );
          });

          setUsers(sortedUsers);
          setError("");
        } catch (err) {
          setError("Failed to load users");
          console.error("Error loading users:", err);
        } finally {
          setIsLoadingUsers(false);
        }
      },
      (err) => {
        setError("Failed to connect to chat service");
        setIsLoadingUsers(false);
        console.error("Firestore error:", err);
      }
    );

    return () => unsubscribe();
  }, [isAdmin, loadLastMessages]);

  // Set selected user from URL, and handle back navigation
  useEffect(() => {
    if (users) {
      if (userId) {
        if (selectedUser?.uid !== userId) {
          const userFromUrl = users.find((u) => u.uid === userId);
          if (userFromUrl) {
            setSelectedUser(userFromUrl);
          }
        }
      } else {
        // If no userId in URL, deselect user
        setSelectedUser(null);
      }
    }
  }, [userId, users, selectedUser]);

  // Load messages for selected user with pagination (last 20 messages)
  const loadMessages = useCallback(
    async (loadOlder = false) => {
      if (!selectedUser) {
        setMessages([]);
        setLastMessageDoc(null);
        setHasMoreMessages(true);
        return;
      }

      if (loadOlder) {
        setIsLoadingOlderMessages(true);
      } else {
        setIsLoadingMessages(true);
        setMessages([]);
        setLastMessageDoc(null);
        setHasMoreMessages(true);
      }

      try {
        let messagesQuery = query(
          collection(db, "messages"),
          where("sender", "in", [currentUser.uid, selectedUser.uid]),
          where("receiver", "in", [currentUser.uid, selectedUser.uid]),
          orderBy("timestamp", "desc"),
          limit(20)
        );

        // If loading older messages, start after the last document
        if (loadOlder && lastMessageDoc) {
          messagesQuery = query(
            collection(db, "messages"),
            where("sender", "in", [currentUser.uid, selectedUser.uid]),
            where("receiver", "in", [currentUser.uid, selectedUser.uid]),
            orderBy("timestamp", "desc"),
            startAfter(lastMessageDoc),
            limit(20)
          );
        }

        const snapshot = await getDocs(messagesQuery);
        const messagesList = snapshot.docs.map((doc) => ({
          uid: doc.id,
          ...doc.data(),
        }));

        if (messagesList.length < 20) {
          setHasMoreMessages(false);
        }

        if (messagesList.length > 0) {
          setLastMessageDoc(snapshot.docs[snapshot.docs.length - 1]);

          if (loadOlder) {
            // Add older messages to the beginning
            setMessages((prev) => {
              const sortedOlder = [...messagesList].sort(
                (a, b) => a.timestamp.toMillis() - b.timestamp.toMillis()
              );
              const combined = [...sortedOlder, ...prev];
              // Remove duplicates
              const unique = combined.filter(
                (msg, index, arr) =>
                  arr.findIndex((m) => m.uid === msg.uid) === index
              );
              return unique;
            });
          } else {
            // Set new messages
            const sortedMessages = [...messagesList].sort(
              (a, b) => a.timestamp.toMillis() - b.timestamp.toMillis()
            );
            setMessages(sortedMessages);
          }
        }

        if (!loadOlder) {
          markMessagesAsRead();
        }
      } catch (err) {
        console.error("Error loading messages:", err);
      } finally {
        setIsLoadingMessages(false);
        setIsLoadingOlderMessages(false);
      }
    },
    [selectedUser, currentUser, lastMessageDoc, markMessagesAsRead]
  );

  // Load messages when user is selected
  useEffect(() => {
    if (selectedUser) {
      loadMessages(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedUser]);

  // Real-time listener for new messages
  useEffect(() => {
    if (!selectedUser) return;

    const messagesQuery = query(
      collection(db, "messages"),
      where("sender", "in", [currentUser.uid, selectedUser.uid]),
      where("receiver", "in", [currentUser.uid, selectedUser.uid]),
      orderBy("timestamp", "desc"),
      limit(1)
    );

    const unsubscribe = onSnapshot(
      messagesQuery,
      (snapshot) => {
        if (!snapshot.empty) {
          const latestMessage = {
            uid: snapshot.docs[0].id,
            ...snapshot.docs[0].data(),
          };

          // Only add if it's a new message (not already in the list)
          setMessages((prev) => {
            const exists = prev.find((msg) => msg.uid === latestMessage.uid);
            if (!exists) {
              return [...prev, latestMessage];
            }
            return prev;
          });

          markMessagesAsRead();
        }
      },
      (err) => {
        console.error("Error listening for new messages:", err);
      }
    );

    return () => unsubscribe();
  }, [selectedUser, currentUser, markMessagesAsRead]);

  // Auto-scroll to bottom
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages]);

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!selectedUser || (!newMessage.trim() && !attachment)) return;

    setIsSending(true);
    try {
      let attachmentUrl = null;
      let attachmentType = null;

      if (attachment) {
        const timestamp = Date.now();
        const fileName = `${timestamp}_${attachment.name}`;
        const storageRef = ref(storage, `chat-attachments/${fileName}`);
        await uploadBytes(storageRef, attachment);
        attachmentUrl = await getDownloadURL(storageRef);
        attachmentType = attachment.type.startsWith("image/") ? "image" : "pdf";
      }

      const messageData = {
        sender: currentUser.uid,
        receiver: selectedUser.uid,
        text: newMessage.trim(),
        attachment: attachmentUrl,
        attachmentType: attachmentType,
        timestamp: serverTimestamp(),
        read: false,
        status: "sent",
      };

      await addDoc(collection(db, "messages"), messageData);

      // Update last message for both users
      const batch = writeBatch(db);

      const selectedUserDocRef = doc(db, "users", selectedUser.uid);
      batch.set(
        selectedUserDocRef,
        {
          lastMessageTimestamp: serverTimestamp(),
          lastMessage: newMessage.trim() || "Attachment",
        },
        { merge: true }
      );

      const currentUserDocRef = doc(db, "users", currentUser.uid);
      batch.set(
        currentUserDocRef,
        {
          lastMessageTimestamp: serverTimestamp(),
          lastMessage: newMessage.trim() || "Attachment",
        },
        { merge: true }
      );

      await batch.commit();

      // Update local last messages state
      const newLastMessage = {
        text: newMessage.trim() || "Attachment",
        timestamp: { toMillis: () => Date.now() },
        sender: currentUser.uid,
      };

      setLastMessages((prev) => ({
        ...prev,
        [selectedUser.uid]: newLastMessage,
      }));

      // Trigger bot response if user is not admin and sent a text message
      if (!isAdmin && newMessage.trim()) {
        try {
          const botUserId = await ensureBotUser();
          
          const shouldRespond = await shouldBotRespond(newMessage.trim(), currentUser.uid, selectedUser.uid);
          
          if (shouldRespond) {
            // Add a small delay to make the conversation feel more natural
            setTimeout(async () => {
              try {
                await sendBotResponse(newMessage.trim(), currentUser.uid);
              } catch (botError) {
                console.error('Error sending bot response:', botError);
              }
            }, 1500); // 1.5 second delay
          }
        } catch (botError) {
          console.error('Error with bot response system:', botError);
        }
      }

      setNewMessage("");
      setAttachment(null);
      setAttachmentPreview(null);
    } catch (err) {
      console.error("Error sending message:", err);
      setError("Failed to send message");
    } finally {
      setIsSending(false);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      setAttachment(file);
      setAttachmentPreview({
        name: file.name,
        size: formatFileSize(file.size),
        type: file.type,
      });
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  const removeAttachment = () => {
    setAttachment(null);
    setAttachmentPreview(null);
  };

  const selectUser = (user) => {
    if (isMobileView) {
      setSelectedUser(user);
    }
    navigate(`/dashboard/chat/${user.uid}`);
  };

  const selectUserLocal = (user) => {
    setSelectedUser(user);
    setNewMessage("");
    setAttachment(null);
    setAttachmentPreview(null);
  };

  const loadOlderMessages = () => {
    if (hasMoreMessages && !isLoadingOlderMessages) {
      loadMessages(true);
    }
  };

  const clearSearch = () => {
    setSearchQuery("");
  };

  const openPreview = (src, type) => {
    setPreviewModal({ show: true, src, type });
  };

  const closePreview = () => {
    setPreviewModal({ show: false, src: "", type: "" });
  };

  const LoadingState = ({ message = "Loading..." }) => (
    <div className="loading-state">
      <Spinner animation="border" role="status" size="sm">
        <span className="visually-hidden">{message}</span>
      </Spinner>
      <p className="loading-text">{message}</p>
    </div>
  );

  const ErrorState = () => (
    <div className="error-state">
      <div className="error-content">
        <h4>Error</h4>
        <p>{error}</p>
        <button
          className="btn btn-outline-danger btn-sm"
          onClick={() => window.location.reload()}
        >
          Retry
        </button>
      </div>
    </div>
  );

  const EmptyChatState = () => (
    <div className="empty-state">
      <IoChatbubbleEllipses size={48} className="empty-icon" />
      <h5>No conversations yet</h5>
      <p>Start chatting with your clients to see messages here.</p>
    </div>
  );

  const MessageStatus = ({ message }) => {
    if (message.sender !== currentUser.uid) return null;

    return (
      <span className="message-status">
        {message.read ? (
          <IoCheckmarkDone className="text-primary" />
        ) : (
          <IoCheckmark className="text-muted" />
        )}
      </span>
    );
  };

  const formatMessageTime = (timestamp) => {
    if (!timestamp) return "";

    // Handle different timestamp types
    if (timestamp.toDate && typeof timestamp.toDate === "function") {
      // Firebase Timestamp
      return format(timestamp.toDate(), "HH:mm");
    } else if (timestamp.toMillis && typeof timestamp.toMillis === "function") {
      // Firebase Timestamp with toMillis method
      return format(new Date(timestamp.toMillis()), "HH:mm");
    } else if (typeof timestamp === "number") {
      // Unix timestamp
      return format(new Date(timestamp), "HH:mm");
    } else if (timestamp instanceof Date) {
      // JavaScript Date object
      return format(timestamp, "HH:mm");
    }

    return "";
  };

  // Handle bot option selection
  const handleBotOptionClick = async (optionId, optionText) => {
    try {
      // Send user's selection as a message
      const messageData = {
        sender: currentUser.uid,
        receiver: selectedUser.uid,
        text: optionText,
        timestamp: serverTimestamp(),
        read: false,
        status: "sent",
      };

      await addDoc(collection(db, "messages"), messageData);

      // Update last message for both users
      const batch = writeBatch(db);

      const selectedUserDocRef = doc(db, "users", selectedUser.uid);
      batch.set(
        selectedUserDocRef,
        {
          lastMessageTimestamp: serverTimestamp(),
          lastMessage: optionText,
        },
        { merge: true }
      );

      const currentUserDocRef = doc(db, "users", currentUser.uid);
      batch.set(
        currentUserDocRef,
        {
          lastMessageTimestamp: serverTimestamp(),
          lastMessage: optionText,
        },
        { merge: true }
      );

      await batch.commit();

      // Trigger bot response based on the selected option
      setTimeout(async () => {
        try {
          await sendBotResponse(optionText, currentUser.uid);
        } catch (botError) {
          console.error('Error sending bot response:', botError);
        }
      }, 1000);

    } catch (error) {
      console.error('Error handling bot option click:', error);
    }
  };

  const renderMessageContent = (message) => {
    if (message.attachment) {
      return (
        <div className="attachment">
          {message.attachmentType === "pdf" ? (
            <a
              href={message.attachment}
              target="_blank"
              rel="noopener noreferrer"
              className="pdf-preview"
            >
              <div className="pdf-icon">
                <IoDocument size={48} color="#e74c3c" />
              </div>
              <p>Download</p>
            </a>
          ) : (
            <img
              src={message.attachment}
              alt="Attachment"
              className="attachment-image"
              onClick={() => openPreview(message.attachment, "image")}
            />
          )}
        </div>
      );
    }

    // Check if message contains interactive options (from bot)
    const hasOptions = message.text && (
      message.text.includes('[1]') || 
      message.text.includes('[2]') || 
      message.text.includes('[3]') ||
      message.text.includes('[a]') || 
      message.text.includes('[b]') || 
      message.text.includes('[c]')
    );

    if (hasOptions && message.sender !== currentUser.uid) {
      // Parse message text to extract options
      const lines = message.text.split('\n');
      const messageText = [];
      const options = [];

      lines.forEach(line => {
        const optionMatch = line.match(/\[([1-3abc])\]\s*(.+)/);
        if (optionMatch) {
          options.push({
            id: optionMatch[1],
            text: optionMatch[2].trim()
          });
        } else if (line.trim()) {
          messageText.push(line);
        }
      });

      return (
        <div className="bot-message-with-options">
          <div className="bot-message-text">
            {messageText.join('\n')}
          </div>
          {options.length > 0 && (
            <div className="bot-options">
              {options.map((option) => (
                <button
                  key={option.id}
                  className="bot-option-button"
                  onClick={() => handleBotOptionClick(option.id, option.text)}
                >
                  <span className="option-id">[{option.id}]</span>
                  <span className="option-text">{option.text}</span>
                </button>
              ))}
            </div>
          )}
        </div>
      );
    }

    // Check if message contains HTML (for bot messages with links)
    if (message.text && (message.text.includes('<a href') || message.text.includes('**'))) {
      return (
        <div 
          className="message-html-content"
          dangerouslySetInnerHTML={{ __html: message.text }}
        />
      );
    }

    return <span>{message.text}</span>;
  };

  if (error) {
    return <ErrorState />;
  }

  const handleBackToUsers = () => {
    navigate("/dashboard/chat");
  };

  return (
    <div className="chat-container">
      <div
        className={`chat-sidebar ${
          isMobileView ? (selectedUser ? "" : "show") : "show"
        }`}
      >
        <div className="chat-header">
          <div className="search-container">
            <IoSearchOutline className="search-icon" />
            <input
              type="text"
              className="search-input"
              placeholder="Search conversations..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            {searchQuery && (
              <button className="clear-search" onClick={clearSearch}>
                <IoClose />
              </button>
            )}
          </div>
        </div>

        <div className="users-list">
          {isLoadingUsers ? (
            <LoadingState message="Loading users..." />
          ) : filteredUsers.length === 0 ? (
            <div className="empty-users">
              <IoChatbubbleEllipses size={48} className="empty-icon" />
              <h5>No conversations found</h5>
              <p>
                {debouncedSearchQuery
                  ? "No conversations match your search"
                  : "No client conversations yet"}
              </p>
              {debouncedSearchQuery && (
                <button className="btn btn-link" onClick={clearSearch}>
                  Clear search
                </button>
              )}
            </div>
          ) : (
            filteredUsers.map((user) => {
              // Get unread count from pre-calculated counts
              const unreadCount = unreadCounts[user.uid] || 0;
              // Get last message from local state or fallback to user data
              const lastMessage = lastMessages[user.uid] || {
                text: user.lastMessage,
                timestamp: user.lastMessageTimestamp,
                sender: null,
              };

              return (
                <div
                  key={user.uid}
                  className={`user-item ${
                    selectedUser?.uid === user.uid ? "active" : ""
                  }`}
                  onClick={() => selectUser(user)}
                >
                  <div className="user-avatar">
                    <img src={user.photoURL} alt={user.name} />
                  </div>
                  <div className="user-info">
                    <div className="user-name-row">
                      <div className="user-name">{user.name}</div>
                      {lastMessage.timestamp && (
                        <div className="user-time">
                          {formatMessageTime(lastMessage.timestamp)}
                        </div>
                      )}
                    </div>
                    {lastMessage.text && (
                      <div className="user-last-message-row">
                        <div className="user-last-message">
                          {lastMessage.text === "Attachment" ? (
                            <>
                              <IoAttach className="last-message-icon" />
                              <span>Attachment</span>
                            </>
                          ) : lastMessage.text.length > 30 ? (
                            lastMessage.text.substring(0, 30) + "..."
                          ) : (
                            lastMessage.text
                          )}
                          {lastMessage.sender === currentUser.uid && (
                            <span className="sent-indicator"> • You</span>
                          )}
                        </div>
                        {unreadCount > 0 && (
                          <div
                            className={`unread-badge ${
                              unreadCount > 9 ? "large" : ""
                            }`}
                          >
                            {unreadCount > 99 ? "99+" : unreadCount}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>

      <div
        className={`chat-main ${isMobileView && !selectedUser ? "hide" : ""}`}
      >
        {selectedUser ? (
          <>
            <div className="chat-header">
              <div className="header-content">
                {isMobileView && (
                  <button className="back-button" onClick={handleBackToUsers}>
                    <IoArrowBack />
                  </button>
                )}
                <div className="header-user-info">
                  <img
                    src={selectedUser.photoURL}
                    alt={selectedUser.name}
                    className="header-user-avatar"
                  />
                  <div className="header-user-details">
                    <h6 className="header-user-name">{selectedUser.name}</h6>
                    <p className="header-user-email">{selectedUser.email}</p>
                  </div>
                </div>
                <button
                  className="ghl-button"
                  onClick={() => {
                    if (selectedUser.ghlContactId) {
                      window.open(
                        `https://app.gohighlevel.com/v2/location/OmOFLA0kINZSjc9gGy9P/contacts/detail//${selectedUser.ghlContactId}`,
                        "_blank"
                      );
                    } else {
                      alert("GHL Contact ID not found for this user");
                    }
                  }}
                >
                  Open in GHL
                </button>
              </div>
            </div>

            <div className="messages-container" ref={messagesContainerRef}>
              {isLoadingMessages ? (
                <LoadingState message="Loading messages..." />
              ) : messages.length === 0 ? (
                <EmptyChatState />
              ) : (
                <div className="messages-list">
                  {hasMoreMessages && (
                    <div className="load-older-container">
                      <button
                        className="load-older-button"
                        onClick={loadOlderMessages}
                        disabled={isLoadingOlderMessages}
                      >
                        {isLoadingOlderMessages ? (
                          <>
                            <Spinner size="sm" animation="border" />
                            <span>Loading older messages...</span>
                          </>
                        ) : (
                          <>
                            <IoChevronUp />
                            <span>Load older messages</span>
                          </>
                        )}
                      </button>
                    </div>
                  )}
                  {messages.map((message) => (
                    <div
                      key={message.uid}
                      className={`message-item ${
                        message.sender === currentUser.uid ? "sent" : "received"
                      }`}
                    >
                      <div className="message-content">
                        {renderMessageContent(message)}
                        <div className="message-meta">
                          <span className="message-time">
                            {formatMessageTime(message.timestamp)}
                          </span>
                          <MessageStatus message={message} />
                        </div>
                      </div>
                    </div>
                  ))}
                  <div ref={messagesEndRef} />
                </div>
              )}
            </div>

            <div className="message-input-container">
              {attachmentPreview && (
                <div className="attachment-preview">
                  <div className="attachment-info">
                    <IoDocument size={24} />
                    <div className="attachment-details">
                      <p className="attachment-name">
                        {attachmentPreview.name}
                      </p>
                      <p className="attachment-size">
                        {attachmentPreview.size}
                      </p>
                    </div>
                    <button
                      className="remove-attachment"
                      onClick={removeAttachment}
                    >
                      <IoClose size={20} />
                    </button>
                  </div>
                </div>
              )}
              <form onSubmit={handleSendMessage} className="message-form">
                <div className="input-group">
                  <input
                    type="text"
                    className="message-input"
                    placeholder="Type your message..."
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    disabled={isSending}
                    maxLength={1000}
                  />
                  <div className="input-actions">
                    <label className="attach-button">
                      <IoAttach size={22} />
                      <input
                        type="file"
                        accept="image/*,.pdf"
                        onChange={handleFileSelect}
                        style={{ display: "none" }}
                      />
                    </label>
                    <button
                      type="submit"
                      className="send-button"
                      disabled={
                        isSending || (!newMessage.trim() && !attachment)
                      }
                    >
                      {isSending ? (
                        <Spinner size="sm" animation="border" />
                      ) : (
                        <IoSend size={24} />
                      )}
                    </button>
                  </div>
                </div>
              </form>
            </div>
          </>
        ) : (
          <div className="no-selection">
            <IoChatbubbleEllipses size={64} className="no-selection-icon" />
            <h4>Select a conversation</h4>
            <p>Choose a user from the sidebar to start chatting</p>
          </div>
        )}
      </div>

      {/* Preview Modal */}
      {previewModal.show && (
        <div className="preview-modal" onClick={closePreview}>
          <div className="preview-content" onClick={(e) => e.stopPropagation()}>
            <button className="preview-close" onClick={closePreview}>
              <IoClose size={24} />
            </button>
            {previewModal.type === "image" ? (
              <img
                src={previewModal.src}
                alt="Preview"
                className="preview-image"
              />
            ) : (
              <object
                data={previewModal.src}
                type="application/pdf"
                className="preview-pdf"
                aria-label="PDF Preview"
              >
                <p>
                  Your browser does not support PDFs.
                  <a href={previewModal.src} download>
                    Download the PDF
                  </a>
                  .
                </p>
              </object>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Chat;
